import { useMemo } from 'react'

import { clsx } from 'clsx'
import { useFormContext, useWatch } from 'react-hook-form'

import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { MemberAvatar } from '../../../common/components/index.js'
import { T } from '../../../i18n/index.js'
import { NEW_CHAT_MEMBERS_COUNT_LIMIT } from '../../constants.js'
import { NewChatFormFields } from '../../types.js'

const MEMBERS_AVATAR_COUNT = 5

export const SelectedMembersList = () => {
  const { control } = useFormContext()
  const members: NewChatFormFields['members'] = useWatch({
    control,
    name: 'members',
  })
  const selectedMembers = useMemo(() => {
    if (!members) {
      return []
    }
    return Array.isArray(members) ? members : [members]
  }, [members])
  const remainingMembersCount = selectedMembers.length - MEMBERS_AVATAR_COUNT
  const membersToShowAvatar = useMemo(() => {
    if (!selectedMembers) {
      return []
    }

    return selectedMembers.slice(0, MEMBERS_AVATAR_COUNT)
  }, [selectedMembers])

  return (
    <div className="flex flex-col gap-y-2">
      <Text className="text-sm text-content-muted">
        <T
          defaultMessage="Group members {count}/{max}"
          id="Messaging.NewGroupChat.MembersField.Label"
          values={{
            count: selectedMembers.length,
            max: NEW_CHAT_MEMBERS_COUNT_LIMIT,
          }}
        />
      </Text>
      <Text className="text-sm text-content-muted font-light">
        <T
          defaultMessage="You can select up to {max} members initially. You may add more people after creating the group."
          id="Messaging.NewGroupChat.MembersField.Description"
          values={{
            max: NEW_CHAT_MEMBERS_COUNT_LIMIT,
          }}
        />
      </Text>
      <div className="flex -space-x-2 items-stretch">
        {membersToShowAvatar.map(member => (
          <MemberAvatar key={member.id} size="2.5x" member={member} />
        ))}
        {remainingMembersCount > 0 && (
          <Avatar
            className={clsx(
              'flex text-content-subdued justify-center items-center min-w-min bg-surface-subdued',
            )}
            size="2.5x"
            fallback={() => {
              return (
                // eslint-disable-next-line react/jsx-no-literals
                <div className="flex text-sm justify-center items-center h-full p-2">
                  +{remainingMembersCount}
                </div>
              )
            }}
          />
        )}
      </div>
    </div>
  )
}
